// The single fetch used by every generated API hook (orval mutator).
// - sends the session cookie (same origin)
// - adds the double-submit CSRF header on state-changing requests
// - turns the error envelope {"error":{"code","message"}} into ApiError

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)atlas_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (method !== "GET" && method !== "HEAD") {
    headers.set("X-CSRF-Token", csrfToken());
  }
  const res = await fetch(url, { ...options, headers, credentials: "same-origin" });
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  let body: any;
  let parsed = true;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      parsed = false;
    }
  }
  if (res.ok && !parsed) {
    // A success that is not JSON (a proxy's HTML page, say) did not come from
    // the API: fail rather than hand the caller undefined as if it worked.
    throw new ApiError(res.status, "bad_response", "The server sent an unexpected response");
  }
  if (!res.ok) {
    const err = body?.error ?? {};
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("atlas:unauthenticated"));
    }
    throw new ApiError(res.status, err.code ?? "http_" + res.status, err.message ?? res.statusText);
  }
  return body as T;
}

export default apiFetch;
